import { errors } from '@lecab/errors';
import { UseMutationResult } from 'react-query';
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import callServer from './serverApi';
import { paths } from '../paths';
import { getFullLanguage, getLanguage } from '../../../utils/language';
import configs from '../../../config';
import { stringsRes } from '../../../resources/strings';
import { ToastWithIcon } from '../toastProvider';
// import { DropinInput } from '../../../core/apiTypes';

type PspData =
    | {
          origin: string;
          returnUrl: string;
          encryptedData: { paymentMethod: any; browserInfo: any };
          channel: 'Web';
      }
    | {
          paymentData: any;
          details: any; // details extracted from previous payment attempt's response
      };

enum AdyenResultCode {
    Authorised = 'Authorised',
    Cancelled = 'Cancelled',
    Refused = 'Refused',
    Error = 'Error',
}

const config = {
    clientKey: configs.envConfig.currentEnv.adyenClientKey,
};

// List langues : https://docs.adyen.com/online-payments/web-components/localization-components#supported-languages
const language = getLanguage();

export async function createCheckoutConfig(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    session: any,
    createDropin: UseMutationResult,
    onSuccess: (data: any) => void,
    onError: (error: any) => void,
    isPro?: boolean,
    contractId?: string,
): Promise<any> {
    const checkoutConfig: CoreOptions = {
        clientKey: config.clientKey,
        session,
        environment: configs.envConfig.currentEnv.adyenEnv,
        showPayButton: true,
        locale: getFullLanguage(),
        // recurringProcessingModel: 'UnscheduledCardOnFile',
        // shopperInteraction: 'Ecommerce',
        paymentMethodsConfiguration: {
            card: {
                hasHolderName: true,
                holderNameRequired: true,
                enableStoreDetails: false,
                // name: 'Credit or debit card',
            },
        },
        onSubmit: (result, component) => {
            // console.log('Starting card flow process');
            localStorage.setItem('adyenConfig', JSON.stringify({ ...config, isPro, contractId }));
            // First step whatever the process basic / 3d secure-1 / 3d secure-2

            // TODO: try to understand why this one bug maybe data not sent in body the same way ??
            // const payload: DropinInput = {
            //     url: '/payment-methods/adyen/authorise',
            //     data: { psp_data: formatPspData(result) },
            // };
            // createDropin.mutate(payload, {
            //     onSuccess: ({ data }) => {
            //         console.log('onSubmit', data);
            //         handleServerResponse(data, component);
            //     },
            //     onError: (e) => {
            //         console.error(e);
            //         //ToastWithIcon('error','Erreur on submit');
            //     },
            // });
            // console.log('onSubmit', { result, component });
            callServer('/payment-methods/adyen/authorise', {
                psp_data: formatPspData(result),
                card_data: isPro ? { holder_id: contractId, holder_type: 'contract' } : undefined,
            })
                .then(({ data }) => {
                    console.log('onSubmit', data);
                    handleServerResponse(data, onSuccess, onError, component);
                })
                .catch((e) => {
                    console.error('authorise', e);
                    ToastWithIcon('error', `${stringsRes.errors.cbRefuse} (Auth)`);
                    component?.setStatus('ready');
                });
        },
        onAdditionalDetails: (result, component) => {
            // this is triggered when submit result comes with specific action (the card issuer requires shopper interaction)
            // console.log('3D final step: Sending secured flow results back to our server ');

            // Last step for completing 3D secure: we send the redirected url data back to our server
            // const payload: DropinInput = {
            //     url: '/payment-methods/adyen/authorise3d',
            //     data: { psp_data: result.data },
            // };
            // createDropin.mutate(payload, {
            //     onSuccess: ({ data }) => {
            //         console.log('onAdditionalDetails', data);
            //         handleServerResponse(data, component);
            //     },
            //     onError: (e) => {
            //         console.error(e);
            //         //ToastWithIcon('error', 'Erreur de 3Ds');
            //     },
            // });
            // console.log('onAdditionalDetails', { result, component });

            callServer('/payment-methods/adyen/authorise3d', {
                psp_data: result.data,
                card_data: isPro ? { holder_id: contractId, holder_type: 'contract' } : undefined,
            })
                .then(({ data }) => {
                    handleServerResponse(data, onSuccess, onError, component);
                })
                .catch((e) => {
                    console.error('authorise3d', e);
                    ToastWithIcon('error', `${stringsRes.errors.cbRefuse} (3Ds)`);
                    component?.setStatus('ready');
                });
        },
        onError: (error, component) => {
            console.error(error.name, error.message, error.stack, component);
            ToastWithIcon('error', error.message);
            component?.setStatus('ready');
        },
    };

    return checkoutConfig;
}

// Last step for completing 3D secure using redirection: we send the redirected url data back to our server
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function handleRedirectResult(detailsResult, createDropin: UseMutationResult, onSuccess, onError): void {
    // console.log('3D redirect final step: Sending secured flow results back to our server ');

    const adyenConfig = JSON.parse(localStorage.getItem('adyenConfig'));

    const pspData: PspData = {
        paymentData: null,
        details: { redirectResult: detailsResult },
    };

    // console.log('handleRedirectResult', { detailsResult });
    // const payload: DropinInput = {
    //     url: '/payment-methods/adyen/authorise3d',
    //     data: { psp_data: pspData },
    // };
    // createDropin.mutate(payload, {
    //     onSuccess: ({ data }) => {
    //         console.log('handleRedirectResult', data);
    //         handleServerResponse(data);
    //     },
    //     onError: (e) => {
    //         console.error(e);
    //         ToastWithIcon('error','Error at final step 3D secure');
    //     },
    // });
    callServer('/payment-methods/adyen/authorise3d', {
        psp_data: pspData,
        card_data: adyenConfig?.isPro ? { holder_id: adyenConfig?.contractId, holder_type: 'contract' } : undefined,
    })
        .then(({ data }) => {
            handleServerResponse(data, onSuccess, onError);
        })
        .catch((e) => {
            console.log(e);
            ToastWithIcon('error', 'Error at final step 3D secure');
            // Leave the redirect/loader page instead of getting stuck on it.
            onError(e);
        });
}

// Handles responses sent from your server to the client
function handleServerResponse(res, onSuccess, onError, component = null) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (component && res.action) {
        console.log('card flow must continue with: ', res.action.type, res);
        component.handleAction(res.action);
    } else if (res.resultCode === AdyenResultCode.Authorised) {
        // Do what you want in case of success, here we will redirect to payment
        console.log('Success', res);
        if (!window.location.href.includes(paths.selfServicePayment)) {
            ToastWithIcon('info', stringsRes.common.added);
        }
        onSuccess(res);
    } else if (res.errorCode) {
        // Tipically Billing is suppose to catch status from Adyen and provide to rider-webapp a suitable message:
        const error = new errors.ClientError(res.errorCode);
        console.log('Error Code', res, error.clientMessage[language]);
        ToastWithIcon('error', error.clientMessage[language]);
        onError(error);
    } else {
        // However if Billing fails to deal with resultCode, let's do it ourselve.
        // For exemple we display default message as a fallback
        console.log('Error', res);
        switch (res.resultCode) {
            case AdyenResultCode.Cancelled:
                ToastWithIcon('error', stringsRes.payment.errors.cancelled);
                break;
            case AdyenResultCode.Refused:
                ToastWithIcon('error', stringsRes.payment.errors.refused);
                break;
            case AdyenResultCode.Error:
            default:
                ToastWithIcon('error', stringsRes.payment.errors.error);
        }
        // The card was not added (cancelled/refused/error), so treat it as a failure rather than
        // navigating the user forward as if it had succeeded.
        onError(res);
    }
}

// The following function is to transform the output from (session creation + input fields) to the existing billing format routes
const formatPspData = (result): PspData => {
    const pspData: PspData = {
        origin: result.data.origin,
        returnUrl: result.data.origin + paths.paymentRedirect,
        encryptedData: result.data,
        channel: 'Web',
    };
    return pspData;
};
